// Typed, same-origin API client. Every request sends the session cookie
// (`credentials: 'include'`). Errors are surfaced as ApiError so screens can
// distinguish "not authenticated" (401), "being finalized" (404/501) and
// genuine failures — we NEVER fabricate data for a missing endpoint.

import type {
  Archive,
  ArchiveDetail,
  ArchiveObject,
  AuditRecord,
  BillingEstimate,
  BurnRequest,
  CommitPreflight,
  Me,
  Notification,
  ObjectVersion,
  Page,
  RetentionPolicy,
  RetrievalEstimate,
  RetrievalResult,
  Schedule,
  ScheduleFreq,
  Snapshot,
  SyncResult,
  S3ImportJob,
  S3ImportRequest,
  S3Source,
  SearchHit,
  StorageQuota,
  TenantPricing,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
  /** 404/501 => backend endpoint not built yet -> honest "being finalized" state. */
  get isNotImplemented(): boolean {
    return this.status === 404 || this.status === 501;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface Query {
  [key: string]: string | number | boolean | null | undefined;
}

function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
  query?: Query,
): Promise<T> {
  const url = buildUrl(path, query);
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init.headers,
      },
      ...init,
    });
  } catch (cause) {
    throw new ApiError(0, 'network error', cause);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : undefined) ?? res.statusText ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export const api = {
  // Identity ---------------------------------------------------------------
  me: () => request<Me>('/v1/me'),

  // Archives ---------------------------------------------------------------
  listArchives: (limit: number, cursor?: string | null) =>
    request<Page<Archive>>('/v1/archives', {}, { limit, cursor }),

  createArchive: (name: string, retentionPolicy: RetentionPolicy) =>
    request<Archive>('/v1/archives', {
      method: 'POST',
      body: JSON.stringify({ name, retentionPolicy }),
    }),

  getArchive: (id: string) => request<ArchiveDetail>(`/v1/archives/${encodeURIComponent(id)}`),

  updateRetention: (id: string, retentionPolicy: RetentionPolicy) =>
    request<Archive>(`/v1/archives/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ retentionPolicy }),
    }),

  deleteArchive: (id: string) =>
    request<void>(`/v1/archives/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Starts a BACKGROUND sync; returns immediately with the job to poll. */
  syncArchive: (id: string) =>
    request<SyncResult>(`/v1/archives/${encodeURIComponent(id)}/sync`, { method: 'POST' }),

  /** Latest sync job for the archive, or null when it was never synchronised. */
  /** Files a burn REQUEST; an operator decides before anything is sealed. */
  requestBurn: (id: string) =>
    request<BurnRequest>(`/v1/archives/${encodeURIComponent(id)}/burn-requests`, { method: 'POST' }),

  burnRequests: (id: string) =>
    request<{ items: BurnRequest[] }>(`/v1/archives/${encodeURIComponent(id)}/burn-requests`),

  syncStatus: (id: string) =>
    request<SyncResult | null>(`/v1/archives/${encodeURIComponent(id)}/sync`),

  listObjects: (archiveId: string, limit: number, cursor?: string | null) =>
    request<Page<ArchiveObject>>(
      `/v1/archives/${encodeURIComponent(archiveId)}/objects`,
      {},
      { limit, cursor },
    ),

  listVersions: (archiveId: string, objectId: string) =>
    request<Page<ObjectVersion>>(
      `/v1/archives/${encodeURIComponent(archiveId)}/objects/${encodeURIComponent(objectId)}/versions`,
    ),

  /** Whether a commit fits in the tenant's daily write budget right now. */
  commitPreflight: (archiveId: string) =>
    request<CommitPreflight>(`/v1/archives/${encodeURIComponent(archiveId)}/commit-preflight`),

  /** URL for the "Deliver DMD" download (binary, streamed by the browser). */
  dmdUrl: (archiveId: string) => `/v1/archives/${encodeURIComponent(archiveId)}/dmd`,

  // Snapshots (sealed, burned DMD images) --------------------------------
  listSnapshots: (archiveId: string) =>
    request<{ items: Snapshot[] }>(`/v1/archives/${encodeURIComponent(archiveId)}/snapshots`),

  /** "Jetzt sichern": seal + burn a snapshot now. */
  createSnapshot: (archiveId: string) =>
    request<Snapshot>(`/v1/archives/${encodeURIComponent(archiveId)}/snapshots`, { method: 'POST' }),

  /** Download URL for a snapshot's retained DMD image. */
  snapshotDownloadUrl: (archiveId: string, snapshotId: string) =>
    `/v1/archives/${encodeURIComponent(archiveId)}/snapshots/${encodeURIComponent(snapshotId)}/download`,

  // Schedule (automated sync + commit) -----------------------------------
  getSchedule: (archiveId: string) =>
    request<Schedule>(`/v1/archives/${encodeURIComponent(archiveId)}/schedule`),

  setSchedule: (
    archiveId: string,
    s: {
      syncFreq: ScheduleFreq;
      syncHour: number;
      syncMinute: number;
      syncWeekday: number;
      syncDom: number;
      commitFreq: ScheduleFreq;
      commitHour: number;
      commitMinute: number;
      commitWeekday: number;
      commitDom: number;
    },
  ) =>
    request<Schedule>(`/v1/archives/${encodeURIComponent(archiveId)}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(s),
    }),

  // Search -----------------------------------------------------------------
  search: (q: string, limit: number, cursor?: string | null) =>
    request<Page<SearchHit>>('/v1/search', {}, { q, limit, cursor }),

  // Audit ------------------------------------------------------------------
  listAudit: (limit: number, cursor?: string | null) =>
    request<Page<AuditRecord>>('/v1/audit', {}, { limit, cursor }),

  // Retrieval --------------------------------------------------------------
  estimateRetrieval: (archiveId: string, objectIds: string[]) =>
    request<RetrievalEstimate>('/v1/retrieval/estimate', {
      method: 'POST',
      body: JSON.stringify({ archiveId, objectIds }),
    }),

  // Fulfil a retrieval: provision a delivery bucket, copy the objects in, and
  // return presigned download links.
  retrieve: (archiveId: string, objectIds: string[]) =>
    request<RetrievalResult>('/v1/retrieval', {
      method: 'POST',
      body: JSON.stringify({ archiveId, objectIds }),
    }),

  // Pricing / billing --------------------------------------------------------
  /** The caller's effective price list, used to quote actions up front. */
  pricing: () => request<TenantPricing>('/v1/pricing'),

  /** Purchased volume vs. what has actually been burned to glass. */
  quota: () => request<StorageQuota>('/v1/quota'),

  // Billing ----------------------------------------------------------------
  billingEstimate: () => request<BillingEstimate>('/v1/billing/estimate'),

  // Notifications ----------------------------------------------------------
  listNotifications: (limit: number, cursor?: string | null) =>
    request<Page<Notification>>('/v1/notifications', {}, { limit, cursor }),

  // S3 import --------------------------------------------------------------
  s3Sources: () => request<Page<S3Source> | { items: S3Source[] }>('/v1/s3import/sources'),

  s3Import: (req: S3ImportRequest) =>
    request<S3ImportJob>('/v1/s3import', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  // The customer portal shows an impersonation banner with an Exit action,
  // so this one admin call legitimately belongs in the customer bundle. Every
  // other /v1/admin/* call lives in adminClient.ts (admin bundle only).
  adminExitImpersonation: () =>
    request<{ status: string }>('/v1/admin/exit-impersonation', { method: 'POST' }),
};

// --- Upload with progress + retry (XHR, since fetch has no upload progress) --

export interface UploadHandle {
  promise: Promise<ArchiveObject>;
  abort: () => void;
}

export function uploadObject(
  archiveId: string,
  file: File,
  opts: { path?: string; onProgress?: (fraction: number) => void } = {},
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<ArchiveObject>((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.path) form.append('path', opts.path);

    xhr.open('POST', `/v1/archives/${encodeURIComponent(archiveId)}/objects`);
    xhr.withCredentials = true;
    xhr.responseType = 'text';

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) opts.onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1);
        resolve(body as ArchiveObject);
      } else {
        const message =
          body && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : xhr.statusText || `HTTP ${xhr.status}`;
        reject(new ApiError(xhr.status, message, body));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'network error'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted'));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}
